import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { getColors } from '../styles/colors';
import eventPopupData from '../data/components/event-popup.json';
import * as S from '../styles/components/EventPopup.styles';

const EventPopup: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const popupContainerRef = useRef<HTMLDivElement>(null);
  
  // Read language from localStorage (managed by Layout)
  const [language, setLanguage] = useState<'de' | 'en'>(() => {
    const stored = localStorage.getItem('siteLanguage');
    return (stored === 'en' || stored === 'de') ? stored : 'de';
  });
  
  // Listen for language changes from Layout
  useEffect(() => {
    const handleLanguageChange = (e: CustomEvent | StorageEvent) => {
      const newLang = e instanceof CustomEvent ? e.detail : localStorage.getItem('siteLanguage');
      if (newLang === 'en' || newLang === 'de') {
        setLanguage(newLang);
      }
    };
    
    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    window.addEventListener('storage', handleLanguageChange as EventListener);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
      window.removeEventListener('storage', handleLanguageChange as EventListener);
    };
  }, []);

  useEffect(() => {
    console.log('EventPopup mounted');
    
    // Popup nur bis 20.10.2025 um 20:08 Uhr anzeigen
    const endDate = new Date('2025-10-20T20:08:00');
    const now = new Date();
    
    console.log('Current time:', now);
    console.log('End time:', endDate);
    console.log('Show popup:', now < endDate);
    
    if (now < endDate) {
      // Show popup after a short delay
      const timer = setTimeout(() => {
        console.log('Opening popup now');
        setIsOpen(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    } else {
      console.log('Popup expired - not showing');
    }
  }, []);

  const handleClose = () => {
    console.log('Closing popup');
    setIsOpen(false);
  };

  const handleContact = () => {
    setIsOpen(false);
    navigate('/kontakt');
  };

  // Get all focusable elements within the popup
  const getFocusableElements = useCallback(() => {
    if (!popupContainerRef.current) return [];
    
    const focusableSelectors = [
      'input:not([disabled]):not([type="hidden"])',
      'button:not([disabled])',
      'a[href]',
      'textarea:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    
    return Array.from(
      popupContainerRef.current.querySelectorAll<HTMLElement>(focusableSelectors)
    ).filter(el => {
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    });
  }, []);

  // ESC-Taste zum Schließen + Focus Trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || !popupContainerRef.current) return;

      // Focus Trap: Keep Tab navigation within the popup
      if (e.key === 'Tab') {
        const focusableElements = getFocusableElements();
        
        if (focusableElements.length === 0) {
          e.preventDefault();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement as HTMLElement;

        const isWithinPopup = popupContainerRef.current.contains(activeElement);

        if (!isWithinPopup) {
          e.preventDefault();
          firstElement.focus();
          return;
        }

        if (e.shiftKey) {
          if (activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
        return;
      }

      // ESC-Taste zum Schließen
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, getFocusableElements]);

  return (
    <AnimatePresence>
      {isOpen && (
        <S.Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <S.PopupContainer
            ref={popupContainerRef}
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <S.CloseButton onClick={handleClose} aria-label={eventPopupData[language].closeButton}>
              ×
            </S.CloseButton>
            <S.PopupContent>
              <S.PopupImage 
                src="/popup/Veranstaltung.png" 
                alt={eventPopupData[language].imageAlt} 
              />
            </S.PopupContent>
            <S.ButtonContainer>
              <S.ContactButton colors={colors} onClick={handleContact}>
                {eventPopupData[language].contactButton}
              </S.ContactButton>
            </S.ButtonContainer>
          </S.PopupContainer>
        </S.Overlay>
      )}
    </AnimatePresence>
  );
};

export default EventPopup;

